import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { existsFilter, pluralReadableTypeName } from '@iotv/core-services';
import { edgeTypeMatrix, EntityProgramme, getGroupToEntityEdgeTypeId, isEntityProgrammeConstructorProps, NodeEdgeMapper, printHrDay, shiftUTCPeriodHrMins, summarizeList, } from '@iotv/datamodel-core';
import { AdjacentType, AppValueTypes, ComparisonType, getObjectHash, getZombieInstanceFromPrimaryKey, LogicalOperatorType, } from '@iotv/iotv-v3-types';
import { deviceEnabledTypes } from '@iotv/payload-definition';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Alert, Card, CardContent, Skeleton, Stack, Tab, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { SimpleTreeView } from '@mui/x-tree-view';
import { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { DialogWrapper } from '../../../../../components/common/DiaglogWrapper';
import { getSingleTypeRecursivePath } from '../../../../../components/EnititySupport/RecusiveEntityGroup/getRecursiveQueryPath';
import { getViewDefintionFromObject } from '../../../../../components/factories/AttributeDefinitions';
import { radioSelector, SelectAll } from '../../../../../components/factories/DataGrid';
import { AppMainViewContext } from '../../../../../ContextProviders/AppContextProvider';
import { isAdminRole } from '../../../../../helpers/access';
import { useModularTimePeriodSelector } from '../../../../../hooks/useModularTimePeriodSelector';
import { useSelectedObjects } from '../../../../../hooks/useSelectedObjects';
import { useTabHandlerStringKeys } from '../../../../../hooks/useTabHandler';
import { ObjectViewType, Severity, } from '../../../../../types/AppTypes';
import { useColumnVisibilityForDataGrid } from '../../ObjectsDataTable/hooks/useColumnVisibility';
import { ObjectTree } from '../../ObjectTree';
import { ObjectDetailCard } from '../../ObjectView/ObjectDetailCard';
import { AdminCardWrapper } from '../common/AdminCardWrapper';
import { ItemTreeOnClickWrapper } from '../common/ItemTreeOnClickWrapper';
import { ProgrammeHeader } from '../common/ProgrammeHeader';
import { Actions } from './Components/Actions';
import { AddSlot } from './Components/AddSlot';
import { ConflictsPopup } from './Components/ConflictsPopup';
import { CreateSlots } from './Components/CreateSlots';
import { DayMask } from './Components/DayMask';
import { EditSlot } from './Components/EditSlot';
import { EntityProgammeTimelineChart } from './Components/EntityTimelineChart';
import { TransactionIndicators } from './Components/TransactionStateIndicators';
import { getStateForSlot, getStylesForSlotStates } from './fns/colourMap';
import { getDataTable } from './fns/getDataTable';
import { defaultProgrammeReducerState, EntityProgrammeContext, } from './Reducers/ProgrammeReducer';
const debug = process.env.REACT_APP_DEBUG && false;
const TransationStateReducer = (prev, next) => {
    return { ...prev, ...next };
};
export const EntityProgrammeInternal = (props) => {
    'use no memo';
    const { viewObject, userSelectedGroup, contextCustomer, allowedActions, isSmallView, transactionFunctions: TransactionFunctions, userFunctions: UserFunctions, } = props;
    // Error: Cannot access refs during render
    const { matchedPrimary } = viewObject;
    const { fromDateValue, toDateValue, setDateRangeMs } = useModularTimePeriodSelector({
        rangeUnit: 'days',
        rangeLength: 1,
        allowedRangeUnits: ['days', 'hours'],
        behaviourFlags: { preventPeriodShiftIntoFuture: false },
        initialToTime: defaultProgrammeReducerState.toDateValue ?? undefined,
    });
    const [{ ipData }] = useContext(AppMainViewContext);
    const [state, dispatch] = useContext(EntityProgrammeContext);
    const { programmeSet, programme, dayMask, scheduler, linkItemToProgramRejectionBehavior, schedulerHash, rows, componentState: { itemConflictRecords }, } = state;
    debug && console.log(`EntityProgrammeInternal rerendered`, itemConflictRecords);
    const [transactionState, dispatchTransactionState] = useReducer(TransationStateReducer, {
        loading: false,
        verifying: false,
        saving: false,
    });
    const objectTreeNodeEdgeMapperRef = useRef(new NodeEdgeMapper());
    const selectableGroups = objectTreeNodeEdgeMapperRef.current.getNodesOfType('Group');
    const selectableItems = programme ? objectTreeNodeEdgeMapperRef.current.getNodesOfType(programme.supportedType) : [];
    const selectableGroupsAndItems = [...selectableGroups, ...selectableItems];
    const slots = scheduler?.getSlots();
    const { state: [[selectedItems, setSelectedItems]], } = useSelectedObjects([], selectableGroupsAndItems);
    const { state: [[selectedSlots, setSelectedSlots]], handlers: { isInSelectedObjects: inSelectedSlots, addToSelectedObjects: addSelectedSlots, removeFromSelectedObjects: removeSelectedSlot, togglePresenceInSelectedObjects: toggleSlot, }, } = useSelectedObjects([], slots);
    useEffect(() => {
        if (selectedSlots.length === 1) {
            const items = scheduler?.getItemsForSlot(selectedSlots[0]);
            setSelectedItems(items ?? []);
        }
        else {
            setSelectedItems([]);
        }
    }, [selectedSlots]);
    const changeState = scheduler?.getChangeState();
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const canAddSlot = scheduler !== null && scheduler.isInitialized && selectedSlots.length === 0;
    const canCreateSlots = scheduler !== null && scheduler.isInitialized && scheduler.getSlots().length === 0;
    const canEdit = scheduler !== null && scheduler.isInitialized && changeState !== undefined && selectedSlots.length === 1;
    const canLink = scheduler !== null && scheduler.isInitialized && changeState !== undefined && selectedSlots.length > 0 && selectedItems.length > 0;
    const canUnlink = scheduler !== null && scheduler.isInitialized && changeState !== undefined && selectedSlots.length > 0 && selectedItems.length > 0;
    const canRemove = scheduler !== null &&
        scheduler.isInitialized &&
        changeState !== undefined &&
        selectedSlots.length > 0 &&
        selectedSlots.every((slot) => scheduler.getItemsForSlot(slot).length === 0);
    const canSave = scheduler !== null &&
        scheduler.isInitialized &&
        changeState !== undefined &&
        (changeState.changed.length > 0 || changeState.new.length > 0 || changeState.deleted.length > 0);
    const loadContextFromDatastore = async (scheduler) => {
        dispatchTransactionState({ loading: true });
        debug && console.log(`EntityProgrammeInternal used effect calling loadContextFromDatastore`);
        const { err, data: context } = await scheduler.loadContextFromDatastore();
        dispatchTransactionState({ loading: false });
        if (err) {
            console.log('EntityProgrammeInternal loadContextFromDatastore ERR:', err.message);
        }
        if (context) {
            const action = {
                type: 'Recalculate',
                value: null,
            };
            dispatch(action);
            // const t0 = Date.now()
            // const loadExistingContextCache = await scheduler.loadExistingContextCache()
            // if ( loadExistingContextCache.data ) {
            //     const cachedContext = loadExistingContextCache.data
            //     console.log(`EntityProgrammeInternal loaded existing context cache in ${ getDurationMinSecStr( t0 )}`, cachedContext.getSummary())
            // } else {
            //     console.log(`EntityProgrammeInternal ERR: Loading existing context`, loadExistingContextCache.err?.message )
            // }
        }
    };
    const ipDataHash = getObjectHash(ipData);
    useEffect(() => {
        if (ipData) {
            const action = {
                type: 'SetIpData',
                value: { ipData },
            };
            dispatch(action);
        }
    }, [ipDataHash]);
    useEffect(() => {
        if (isEntityProgrammeConstructorProps(matchedPrimary)) {
            debug && console.log(`EntityProgrammeInternal setting programme from matchedPrimary`, matchedPrimary);
            const action = {
                type: 'SetProgramme',
                value: { programme: new EntityProgramme(matchedPrimary) },
            };
            debug && console.log(`EntityProgrammeInternal dispatching`, action);
            dispatch(action);
        }
        else {
            debug && console.log(`EntityProgrammeInternal deprecated: creating programme from matchedPrimary`, matchedPrimary);
        }
    }, [matchedPrimary?.sk]);
    useEffect(() => {
        debug && console.log(`EntityProgrammeInternal used effect on interval`, fromDateValue, toDateValue);
        const action = {
            type: 'SetDates',
            value: { fromDateValue, toDateValue },
        };
        dispatch(action);
    }, [fromDateValue, toDateValue]);
    useEffect(() => {
        if (scheduler) {
            if (scheduler.isInitialized) {
            }
            else {
                dispatchTransactionState({ loading: true });
                debug && console.log(`EntityProgrammeInternal used effect calling loadContextFromDatastore`);
                loadContextFromDatastore(scheduler);
            }
        }
    }, [schedulerHash]);
    const dataTable = rows ? getDataTable({ rows }) : null;
    const togglePresenceInSelectedObjectsShim = (v, id) => {
        toggleSlot(id);
    };
    const selectSlot = {
        valueGetter: (value, row, column, apiRef) => inSelectedSlots({ id: row.id }),
        renderCell: (gridCellParams) => radioSelector({
            gridCellParams,
            valueSetter: togglePresenceInSelectedObjectsShim,
        }),
        renderHeader: () => (_jsx(SelectAll, { items: slots ?? [],
            selectedObjects: selectedSlots,
            setSelectedObjects: setSelectedSlots })),
    };
    const handleLinkSelectedItemsToSlots = async ({ items, slots, rejectionBehavior }) => {
        const timeslots = slots.map((slot) => slot.timeslot);
        const action = {
            type: 'LinkItemsToSlots',
            value: { successfulPrechecks: [] },
        };
        if (scheduler) {
            dispatchTransactionState({ verifying: true });
            const preAllocationCheckRes = await scheduler.preCheckAddItemsToTimeslots({
                existingItems: items,
                proposedTimeslots: timeslots,
                rejectionBehavior,
            });
            const { err, data } = preAllocationCheckRes;
            if (err) {
                // TOOD surface errs
                debug && console.log(`EntityProgrammeInternal ERR: handleLinkSelectedItemsToSlots`, err.message);
            }
            if (data) {
                const preCheckResults = data;
                preCheckResults.forEach((checkRes) => {
                    if (checkRes?.item && checkRes.timeslot) {
                        action.value.successfulPrechecks.push(checkRes);
                        debug && console.log('EntityProgrammeInternal add item to programme res', checkRes);
                    }
                    else {
                        const { conflictingSlots, item } = checkRes;
                        debug && console.log('EntityProgrammeInternal rejection', conflictingSlots);
                        const itemConflictsRecord = {
                            item,
                            conflictingSlots,
                        };
                        const conflictAction = {
                            type: 'SetConflictRecords',
                            value: { itemConflictsRecord },
                        };
                        dispatch(conflictAction);
                    }
                });
                if (preCheckResults.length === 0) {
                    debug && console.log(`EntityProgrammeInternal WARN: no preCheckResults for ${items.map((item) => item.sk).join(', ')}`);
                }
                if (action.value.successfulPrechecks.length > 0) {
                    debug && console.log('EntityProgrammeInternal dispatching link request', action);
                    dispatch(action);
                }
            }
            dispatchTransactionState({ verifying: false });
        }
        else {
            debug && console.log(`EntityProgrammeInternal WARN: handleLinkSelectedItemsToSlots no scheduler`);
        }
    };
    const handleUnlinkItemsFromSlot = async ({ slots }) => {
        const timeslots = slots.map((slot) => slot.timeslot);
        const action = {
            type: 'UnlinkItemsFromMultipleSlots',
            value: { timeslots },
        };
        if (scheduler) {
            dispatch(action);
        }
    };
    const handleRemoveSlots = async ({ slots }) => {
        const timeslots = slots.map((slot) => slot.timeslot);
        const action = {
            type: 'RemoveSlots',
            value: { timeslots },
        };
        if (scheduler) {
            dispatch(action);
        }
    };
    const handleEditSlot = () => {
        setEditDialogOpen(true);
    };
    const handleSave = async () => {
        if (scheduler) {
            dispatchTransactionState({ saving: true });
            debug && console.log('EntityProgrammeInternal handleSave change state', scheduler.getChangeState());
            const res = await scheduler?.persistChangeState();
            debug && console.log('EntityProgrammeInternal handleSave res', res);
            dispatchTransactionState({ saving: false });
            if (res.writeRes.err || !res.writeRes.data)
                UserFunctions.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    label: 'RollingProgrammed saved',
                    content: `Error saving ${programme?.name}: ${res.writeRes.err?.message}`,
                    severity: Severity.error,
                });
            else
                UserFunctions.setUserMessage({
                    id: v4(),
                    type: 'SnackBar',
                    label: 'RollingProgrammed saved',
                    content: `Saved ${programme?.name}`,
                    severity: Severity.success,
                });
        }
    };
    const handleSetDaymask = async (dayMask) => {
        debug && console.log(`EntityProgrammeInternal handleSetDaymask got`, dayMask);
        if (programmeSet === null)
            return debug && console.log('EntityProgrammeInternal handleSetDaymask: NO PROGRAMME SET');
        if (scheduler === null)
            return debug && console.log('EntityProgrammeInternal handleSetDaymask: NO SCHEDULER');
        dispatchTransactionState({ verifying: true });
        const { err, data } = await scheduler.changeDayMask(dayMask);
        if (err) {
            debug && console.log(`EntityProgrammeInternal handleSetDaymask ERR:`, err.message);
        }
        if (data) {
            const newDaymask = data;
            debug && console.log(`EntityProgrammeInternal handleSetDaymask DATA:`, data);
            const action = {
                type: 'SetDaymask',
                value: { dayMask: newDaymask },
            };
            dispatch(action);
        }
        dispatchTransactionState({ verifying: false });
    };
    const handleCreateSlots = () => {
        setCreateDialogOpen(true);
    };
    const handleAddSlot = () => {
        setAddDialogOpen(true);
    };
    const getUtcToLocalTransformedRows = () => {
        let datatableRows = [];
        if (scheduler) {
            const slots = scheduler.getSlots();
            datatableRows = slots.map((slot) => {
                const { utc_start_hour, utc_start_min, duration_mins } = slot.timeslot;
                const { utc_start_hour: future_start_hour, utc_start_min: local_start_min } = shiftUTCPeriodHrMins(slot.timeslot, ipData?.utcMinsOffset ?? 0);
                const local_start_hour = future_start_hour % 24;
                const entities = scheduler.getItemsForSlot(slot);
                return {
                    id: slot.id,
                    duration_mins,
                    entities,
                    local_start_hour,
                    local_start_min,
                    utc_start_hour,
                    utc_start_min,
                };
            });
        }
        return datatableRows;
    };
    const getRowClassName = useCallback((params) => {
        if (scheduler) {
            const slot = scheduler.getSlots().find((s) => s.id === params.row.id);
            if (slot)
                return `${getStateForSlot(programme, scheduler, slot)}`;
        }
        return '';
    }, [scheduler, programme]);
    const common = {
        sortable: false,
        resizable: false,
        filterable: false,
        disableColumnMenu: true,
    };
    const columns = [
        {
            field: 'id',
            ...common,
            ...selectSlot,
            width: 50,
            defaultVisible: true,
            hideSortIcons: true,
        },
        {
            field: 'utc_start_hour',
            headerName: 'hour (UTC)',
            ...common,
            width: 60,
            defaultVisible: false,
        },
        {
            field: 'utc_start_min',
            headerName: 'minute (UTC)',
            ...common,
            width: 60,
            defaultVisible: false,
        },
        {
            field: 'local_start_hour',
            headerName: 'start hour',
            ...common,
            width: 60,
            defaultVisible: false,
        },
        {
            field: 'local_start_min',
            headerName: 'start minute',
            ...common,
            width: 60,
            defaultVisible: false,
        },
        {
            field: 'displayTIme',
            headerName: 'Start time',
            ...common,
            width: 85,
            defaultVisible: true,
            valueGetter: (value, row) => `${printHrDay(row.local_start_hour, row.local_start_min)}`,
        },
        {
            field: 'duration_mins',
            headerName: 'Duration',
            ...common,
            minWidth: 85,
            defaultVisible: !isSmallView,
        },
        {
            field: 'entities',
            headerName: `Scheduled ${programme?.supportedType ? pluralReadableTypeName(programme.supportedType) : 'Entity'}`,
            ...common,
            flex: 1,
            defaultVisible: true,
            valueGetter: (value) => summarizeList(value, 8),
        },
    ];
    const externalColumnVisibilityRef = useRef(Object.fromEntries(columns.map((c) => [c.field, c.defaultVisible])));
    const [columnVisibilityModel, setColumnVisibilityModel, onColumnVisibilityModelChange] = useColumnVisibilityForDataGrid({
        dataTableReference: `EntityProgramme`,
        objectTypeId: programme?.supportedType ?? 'Generic',
        viewType: ObjectViewType.List,
        externalColumnVisibility: externalColumnVisibilityRef.current,
    });
    const datatableRows = getUtcToLocalTransformedRows();
    const cardPadding = 16;
    const headerHeight = 48;
    const tabPadding = 8;
    const handleEnabledChange = (checked) => {
        const action = {
            type: 'MutateProgramme',
            value: { delta: { enabled: checked } },
        };
        dispatch(action);
    };
    const components = {
        header: _jsx(ProgrammeHeader, { programmeSet: programmeSet, programme: programme, setEnabled: (enabled) => handleEnabledChange(enabled) }),
        dayMask: _jsx(DayMask, { dayMask: dayMask, setDaymask: handleSetDaymask }),
        timeline: programme && scheduler && scheduler.isInitialized ? (_jsx(EntityProgammeTimelineChart, { dataTable: dataTable, fragmentProps: { style: { height: 50 + 41, width: '100%' } }, timelineOptions: { timeline: { showRowLabels: false } } })) : (_jsx(Skeleton, { variant: "rounded", height: 41 + 'px', width: '100%', sx: { marginBlockEnd: '50px' } })),
        indicators: _jsx(TransactionIndicators, { transactionState: transactionState, loaded: programme !== null }),
        schedule: programme && scheduler ? (_jsx(DataGrid
        /**
         * ISSUE: when reducing the number of slots, and error like No row with id #8 found at `Object.getRowParams`
         * This is quite possibly addressed in [[DataGrid] Fix issue with Invalid Row Id #16631](https://github.com/mui/mui-x/pull/16631)
         */
        , { rows: datatableRows,
            columns,
            columnVisibilityModel,
            onColumnVisibilityModelChange,
            autoPageSize: true,
            maxRows: 10,
            density: 'compact',
            sortModel: [{ field: 'local_start_hour', sort: 'asc' }],
            onRowClick: ({ id }) => {
                const slot = scheduler.getSlots().find((slot) => slot.id === id);
                if (slot) {
                    inSelectedSlots(slot) ? removeSelectedSlot(slot) : addSelectedSlots(slot);
                }
            },
            getRowClassName,
            sx: Object.values(getStylesForSlotStates()) }, `${programme.sk}_${datatableRows.length}`)) : (_jsx(Skeleton, { variant: "rounded", height: '100%', width: '100%' })),
        groupsAndEntities: contextCustomer && programme ? (_jsxs(Stack, { height: '100%', children: [programme.supportedType ? (_jsx(Typography, { variant: "h6", children: isSmallView ? `Schedulable Items` : `Schedulable ${pluralReadableTypeName(programme.supportedType)}` })) : (_jsx(Alert, { color: 'info', children: `Please set supported type on Details Tab` })), _jsx(SimpleTreeView, { sx: { height: '100%', overflowY: 'auto' }, children: _jsx(ObjectTree, { selectable: selectedSlots.length > 0,
                        setSelectedNodeIds: (sks) => setSelectedItems(sks
                            .map((sk) => objectTreeNodeEdgeMapperRef.current.getItem(getZombieInstanceFromPrimaryKey(sk)))
                            .filter(existsFilter)),
                        selectedNodeIds: [...selectedItems.map((item) => item.sk)],
                        nodeEdgeMapper: objectTreeNodeEdgeMapperRef.current,
                        hideTopStep: true,
                        contextObjects: [contextCustomer],
                        queryPaths: [
                            {
                                stepQuery: {
                                    objectTypeId: 'Group',
                                    edgeTypeId: edgeTypeMatrix.Customer.Group.customer_has_group.edgeTypeId,
                                    adjacencyType: AdjacentType.CHILD,
                                    nodeFilters: [
                                        {
                                            filters: [
                                                {
                                                    key: 'supportedType',
                                                    predicate: ComparisonType.EQUALS,
                                                    value: programme.supportedType,
                                                },
                                            ],
                                            setOperation: LogicalOperatorType.AND,
                                        },
                                    ],
                                },
                                nextSteps: [
                                    {
                                        stepQuery: {
                                            objectTypeId: programme.supportedType,
                                            edgeTypeId: getGroupToEntityEdgeTypeId(programme.supportedType),
                                        },
                                    },
                                    ...getSingleTypeRecursivePath({
                                        groupObjectType: 'Group',
                                        containedObjectType: programme.supportedType,
                                        ContainedTypeInstanceClickWrapper: ItemTreeOnClickWrapper,
                                    }),
                                ],
                            },
                        ] }) }, "groupsAndEntities")] })) : (_jsx(Skeleton, { variant: "rounded", height: '100%', width: '100%' })),
        buttons: (_jsx(Actions, { canAdd: canAddSlot, add: handleAddSlot, canCreate: canCreateSlots, create: handleCreateSlots, canEdit: canEdit, edit: () => handleEditSlot(), canLink: canLink, link: () => handleLinkSelectedItemsToSlots({
                items: selectedItems,
                slots: selectedSlots,
                rejectionBehavior: linkItemToProgramRejectionBehavior,
            }), canUnlink: canUnlink, unlink: () => handleUnlinkItemsFromSlot({ slots: selectedSlots }), canRemove: canRemove, remove: () => handleRemoveSlots({ slots: selectedSlots }), canSave: canSave, save: handleSave, loaded: programme !== null })),
    };
    /**
     * CRA — I know the specific nesting of `<Stack />`'s and height controls and flexboxes and container queries, etc., looks absolutely mad
     * Trust me, it works. It has to work in 6 different scenarios:
     *
     * 1) Admin view, with a large screen: expand to fill height, no scrollbars, inner components have scrollbars when/if necessary
     * 2) User view, with a large screen: expand to fill height, screen scrolls, inner components have scrollbars when/if necessary
     * 3) Admin view, with a small screen: at least 10 table rows height, scrollbar if necessary, inner components have scrollbars when/if necessary
     * 4) User view, with a small screen: at least 10 table rows height, scrollbar if necessary, inner components have scrollbars when/if necessary
     * 5) Admin view, with a phone screen: at least 10 table rows height, screen scrolls, inner components have scrollbars when/if necessary
     * 6) User view, with a phone screen: at least 10 table rows height, screen scrolls, inner components have scrollbars when/if necessary
     */
    const mainView = () => (_jsxs(Stack, { minHeight: isSmallView ? '150cqh' : isAdminRole(userSelectedGroup) ? `calc(100cqh - ${cardPadding * 2}px - ${headerHeight}px - ${tabPadding * 2}px)` : '100cqh', gap: 1, children: [_jsxs(AdminCardWrapper, { children: [_jsxs(Stack, { direction: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', sx: { marginBlockEnd: 1 }, children: [components.header, components.dayMask] }), components.timeline] }), _jsx(AdminCardWrapper, { cardProps: { sx: { flexGrow: 1, display: 'flex', flexDirection: 'row' } }, cardContentProps: { sx: { boxSizing: 'border-box', flexGrow: 1 } }, children: _jsxs(Stack, { height: '100%', flexGrow: 1, gap: 1, children: [components.indicators, _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', flexGrow: 1, gap: 1, sx: { flexBasis: isSmallView ? '60.5em' : '30em', containerType: 'size' }, children: [_jsx("div", { style: { width: isSmallView ? '100cqw' : '50cqw', minHeight: '30em' }, children: components.schedule }), _jsx("div", { style: { width: isSmallView ? '100cqw' : '50cqw', minHeight: '30em' }, children: components.groupsAndEntities })] })] }) }), components.buttons, itemConflictRecords && _jsx(ConflictsPopup, { itemConflictRecords }), _jsx(DialogWrapper, { header: 'Add Slot',
                open: addDialogOpen,
                setClosed: () => setAddDialogOpen(false),
                fullWidth: false, children: _jsx(AddSlot, { close: () => setAddDialogOpen(false) }) }), _jsx(DialogWrapper, { header: 'Create Slots',
                open: createDialogOpen,
                setClosed: () => setCreateDialogOpen(false),
                fullWidth: false, children: _jsx(CreateSlots, { close: () => setCreateDialogOpen(false) }) }), selectedSlots.length === 1 && (_jsx(DialogWrapper, { header: 'Edit Slot',
                open: editDialogOpen,
                setClosed: () => setEditDialogOpen(false),
                fullWidth: false, children: _jsx(EditSlot, { slot: selectedSlots[0], close: () => setEditDialogOpen(false) }) }))] }));
    const viewDefinition = matchedPrimary ? getViewDefintionFromObject(matchedPrimary) : {};
    viewDefinition.supportedType = {
        key: 'supportedType',
        editable: true,
        label: 'Supported Type',
        defaultValue: matchedPrimary?.supportedType,
        type: AppValueTypes.ENUM,
        enumKV: Object.fromEntries(Object.keys(deviceEnabledTypes).map((k) => [k, k])),
    };
    const tabDefinitions = {
        Programmes: () => mainView(),
        Details: () => matchedPrimary ? (_jsx(ObjectDetailCard, { matchedPrimary,
            mostProximateCustomer: undefined,
            viewDefinition,
            transactionFunctions: TransactionFunctions,
            userFunctions: UserFunctions,
            allowedActions })) : null,
    };
    const tabsToDisplay = tabDefinitions;
    const { state: [[tabValue, setTabValue]], handlers: { handleTabChange }, } = useTabHandlerStringKeys(tabsToDisplay);
    return isAdminRole(userSelectedGroup) ? (_jsx(Card, { elevation: 3, children: _jsx(CardContent, { sx: { padding: 0, ['&:last-child']: { padding: 2 } }, children: _jsxs(TabContext, { value: tabValue, children: [_jsx(TabList, { onChange: handleTabChange, children: Object.entries(tabsToDisplay)
                            .filter(([tabName, RFC], i) => RFC !== null)
                            .map(([tabName, _RFC], i) => (_jsx(Tab, { value: tabName, label: tabName }, `tabHeader_${i}`))) }), Object.entries(tabsToDisplay)
                        .filter(([_k, c]) => !!c)
                        .map(([k, component]) => (_jsx(TabPanel, { value: k, sx: { padding: 1 }, children: component() }, `tabBase_${k}`)))] }) }) })) : (mainView());
};
